import { useState, useEffect, useCallback } from 'react';
import { Shield, ChevronLeft, ChevronRight, LogOut, Radio, Menu } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../../stores/appStore';
import { useNavigate } from 'react-router-dom';
import { adminNavGroups, NavItem } from './adminNavConfig';

interface AdminLayoutProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  children: React.ReactNode;
  eventCount?: number;
  userCount?: number;
}

const SIDEBAR_EXPANDED = 260;
const SIDEBAR_COLLAPSED = 64;

const AdminLayout: React.FC<AdminLayoutProps> = ({
  activeTab,
  onTabChange,
  children,
  eventCount,
  userCount,
}) => {
  const navigate = useNavigate();
  const { user, setUser } = useAppStore();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  // Auto-collapse on medium screens
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      if (w >= 1280) {
        setCollapsed(false);
        setMobileOpen(false);
      } else if (w >= 1024) {
        setCollapsed(true);
        setMobileOpen(false);
      } else {
        setMobileOpen(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('auth_token');
    setUser(null);
    navigate('/');
  }, [setUser, navigate]);

  // Find active item info for breadcrumb
  const activeGroup = adminNavGroups.find(g => g.items.some(i => i.id === activeTab));
  const activeItem = activeGroup?.items.find(i => i.id === activeTab);

  // Badge helper
  const getBadge = (item: NavItem) => {
    if (item.id === 'events' && eventCount !== undefined) return String(eventCount);
    if (item.id === 'users' && userCount !== undefined) return String(userCount);
    return item.badge;
  };

  const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;

  const renderNavItem = (item: NavItem) => {
    const isActive = activeTab === item.id;
    const Icon = item.icon;
    const badge = getBadge(item);

    return (
      <li key={item.id} className="relative">
        <button
          onClick={() => {
            onTabChange(item.id);
            if (isMobile) setMobileOpen(false);
          }}
          onMouseEnter={() => collapsed && setHoveredItem(item.id)}
          onMouseLeave={() => setHoveredItem(null)}
          className={`
            w-full flex items-center gap-3 rounded-lg transition-all duration-150
            ${collapsed ? 'justify-center px-2 py-2.5' : 'px-3 py-2'}
            ${isActive
              ? 'bg-purple-600/20 text-white border-l-2 border-purple-500'
              : 'text-gray-300 hover:bg-gray-800 border-l-2 border-transparent'
            }
          `}
        >
          <Icon size={20} className="flex-shrink-0" />
          {!collapsed && (
            <>
              <span className="text-sm font-medium truncate">{item.label}</span>
              {badge && (
                <span className="ml-auto bg-gray-700 text-gray-300 text-xs px-1.5 py-0.5 rounded-full">
                  {badge}
                </span>
              )}
            </>
          )}
        </button>

        {/* Tooltip on collapsed */}
        {collapsed && hoveredItem === item.id && (
          <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap pointer-events-none">
            {item.label}
          </div>
        )}
      </li>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full bg-gray-900">
      {/* Logo */}
      <div className={`flex items-center gap-2.5 px-4 py-5 border-b border-gray-800 ${collapsed ? 'justify-center' : ''}`}>
        <Shield size={24} className="text-purple-400 flex-shrink-0" />
        {!collapsed && (
          <span className="text-lg font-bold text-white tracking-tight">PRO-Act</span>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto px-2 pb-4 scrollbar-thin">
        {adminNavGroups.map(group => (
          <div key={group.id}>
            {!collapsed && (
              <h3 className="px-4 mt-6 mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                {group.label}
              </h3>
            )}
            {collapsed && <div className="mt-4" />}
            <ul className="space-y-0.5">
              {group.items.map(renderNavItem)}
            </ul>
          </div>
        ))}
      </nav>

      {/* Collapse toggle - desktop only */}
      {!isMobile && (
        <button
          onClick={() => setCollapsed(c => !c)}
          className="flex items-center justify-center py-3 border-t border-gray-800 text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
        >
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      )}
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Desktop sidebar */}
      <aside
        className="hidden lg:block flex-shrink-0 transition-all duration-200"
        style={{ width: sidebarWidth }}
      >
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: -SIDEBAR_EXPANDED }}
              animate={{ x: 0 }}
              exit={{ x: -SIDEBAR_EXPANDED }}
              transition={{ type: 'tween', duration: 0.2 }}
              className="fixed inset-y-0 left-0 z-50 lg:hidden"
              style={{ width: SIDEBAR_EXPANDED }}
            >
              {sidebarContent}
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="flex items-center justify-between h-14 px-4 sm:px-6 bg-white border-b border-gray-200 flex-shrink-0">
          {/* Left: hamburger + breadcrumb */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu size={22} />
            </button>
            <nav className="text-sm text-gray-500">
              {activeGroup && (
                <>
                  <span>{activeGroup.label}</span>
                  <span className="mx-1.5">/</span>
                </>
              )}
              <span className="text-gray-900 font-medium">{activeItem?.label || activeTab}</span>
            </nav>
          </div>

          {/* Right: Quick Live + user + logout */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => onTabChange('live')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-lg transition-colors text-sm font-medium"
            >
              <Radio size={16} />
              <span className="hidden sm:inline">Live</span>
            </button>
            <span className="text-sm text-gray-600 hidden sm:inline">
              {user?.name || user?.email}
            </span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 text-gray-500 hover:text-red-600 transition-colors text-sm"
            >
              <LogOut size={18} />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
